import React from 'react';
import { FaTrash, FaEdit, FaCopy } from 'react-icons/fa';
import { BsLightningCharge } from 'react-icons/bs';
import { Variable } from '@/types/TVariables';
import { Tooltip, message } from 'antd';

interface VariableItemProps {
	name: string;
	data: Variable;
	onEdit: (name: string, data: Variable) => void;
	onDelete: (name: string) => void;
}

const VariableItem: React.FC<VariableItemProps> = ({ name, data, onEdit, onDelete }) => {
	const handleCopy = () => {
		navigator.clipboard.writeText(`{{${name}}}`);
		message.success('Variable copied to clipboard!');
	};

	return (
		<div
			className="group flex flex-col sm:flex-row items-start gap-3 p-3 sm:p-4 
                    bg-white rounded-xl border border-slate-100 hover:border-indigo-100 
                    hover:shadow-sm transition-all"
		>
			{/* Icon */}
			<div className="p-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shrink-0">
				<BsLightningCharge className="text-indigo-500 text-lg" />
			</div>

			{/* Main Content */}
			<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full min-w-0">
				{/* Variable Name Section */}
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-xs font-medium text-slate-400 uppercase">Variable:</span>
					<Tooltip title="Click to copy">
						<div
							onClick={handleCopy}
							className="flex items-center gap-2 px-2 py-1.5 bg-indigo-50 hover:bg-indigo-100 
                       rounded-lg cursor-pointer transition-colors max-w-[200px] sm:max-w-none"
						>
							<code className="font-mono text-sm font-medium text-indigo-600 truncate">{`{{${name}}}`}</code>
							<FaCopy className="text-indigo-400 text-xs shrink-0" />
						</div>
					</Tooltip>
					{data.isMandatory && (
						<span className="px-2 py-0.5 bg-red-50 text-red-500 text-xs font-medium rounded-full shrink-0">
							Required
						</span>
					)}
				</div>

				{/* Separator */}
				<div className="hidden sm:block h-5 w-px bg-slate-200 mx-2" />

				{/* Default Value Section */}
				<div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
					<span className="text-xs font-medium text-slate-400 uppercase shrink-0">Value:</span>
					<div className="px-2 py-1.5 bg-slate-50 text-slate-600 text-sm rounded-lg truncate flex-1 max-w-full">
						{data.defaultValue || 'No default value'}
					</div>
				</div>

				{/* Actions */}
				<div className="flex items-center gap-1 mt-2 sm:mt-0 sm:ml-auto">
					<Tooltip title="Edit Variable">
						<button
							onClick={() => onEdit(name, data)}
							className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 
                       rounded-lg transition-all"
						>
							<FaEdit size={15} />
						</button>
					</Tooltip>
					<Tooltip title="Delete Variable">
						<button
							onClick={() => onDelete(name)}
							className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 
                       rounded-lg transition-all"
						>
							<FaTrash size={15} />
						</button>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default VariableItem;
